import { getFunctions, httpsCallable } from "firebase/functions";
import { ChangeEvent, useMemo, useState } from "react";
import debounce from "debounce-promise";
import React from "react";

import { Loader } from "@heathmont/moon-core-tw";
import { getCompetencyId } from "@/lib/utils";
import XIcon from "./icons/XIcon";
import SearchIcon from "./icons/SearchIcon";

type SearchResult = {
  id: string;
  name: string;
  level: number;
  description: string;
  content: string;
  score: number;
};

type EmbeddedSearchProps = {
  setSearchResultIds: (resultIds: string[]) => void;
  trackSearch: (searchTerm: string, searchResults?: SearchResult[]) => void;
};

const EmbeddedSearch = ({
  setSearchResultIds,
  trackSearch,
}: EmbeddedSearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResultsData, setSearchResultsData] = useState<SearchResult[]>(
    [],
  );

  const debouncedSearchFn = useMemo(() => {
    const functions = getFunctions();
    return debounce(httpsCallable(functions, "search"), 300);
  }, []);

  const clearSearch = () => {
    setSearchTerm("");
    setSearchResultIds([]);
  };

  const onSearch = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const searchQuery = event.target.value;
    setSearchTerm(searchQuery);

    const trimmedSearchQuery = searchQuery.trim();
    if (!trimmedSearchQuery || trimmedSearchQuery.length < 3) {
      setSearchResultIds([]);
      return;
    }

    try {
      setIsLoading(true);
      const result = await debouncedSearchFn({ text: trimmedSearchQuery });
      const data = result.data as SearchResult[];
      setSearchResultsData(data);
      const ids = data.map((searchResult) => {
        return getCompetencyId(searchResult.id);
      });
      setSearchResultIds(ids);
    } catch {
      setSearchResultIds([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative col-span-full mx-auto w-full max-w-[716px] px-4 medium:col-span-6 medium:col-start-4">
      <SearchIcon fill="#6D6D6D" className="absolute left-7 top-4" />
      {isLoading && (
        <div
          className="absolute right-16 top-4 h-8 w-8"
          role="status"
          aria-label="loading"
        >
          <Loader size="xs" color="border-[#6D6D6D]" />
        </div>
      )}

      {!!searchTerm && (
        <button
          className="absolute right-8 top-3 h-8 w-8 rounded-full p-1 hover:bg-black/5 active:bg-black/10"
          onClick={clearSearch}
          aria-label="Clear Search"
        >
          <XIcon />
        </button>
      )}

      <input
        placeholder="Enter a subject, theme or project idea"
        value={searchTerm}
        onChange={onSearch}
        className="h-[56px] w-full rounded-full border border-tab-border bg-white py-2 pl-10 pr-6 shadow-md focus:border-[#B0B0B0]"
        name="Search"
        autoComplete="off"
        aria-label="Search"
        onBlur={() => trackSearch(searchTerm, searchResultsData)}
      />
    </div>
  );
};

export default EmbeddedSearch;
